import arrowRightIcon from '@raison/static/icons/arrow-right-s-line.svg';
import React, {FC, FunctionComponent, ReactNode} from 'react';
import {cn, RsnSkeleton} from '~/library/ui-kit';
import {createPolymorphicComponent, PolyElement} from '../../Utilities/createPolymorphicComponent';
import InlineSVG from '../InlineSvg/InlineSvg';

interface RsnPaneBaseProps {
    children: ReactNode;
    className?: string;
}

export function RsnPaneBase({children, className, ...rest}: RsnPaneBaseProps) {
    return (
        <PolyElement {...rest} className={cn('RsnPane__root', className)}>
            {children}
        </PolyElement>
    );
}

function Icon({src}: {src: string}) {
    return (
        <div className={'RsnPane__icon'}>
            <img src={src} width={'100%'} height={'100%'} />
        </div>
    );
}

function SVGIcon({src, className}: {src: string; className?: string}) {
    return (
        <div className={cn('RsnPane__icon', className)}>
            <InlineSVG src={src} width={'100%'} height={'100%'} />
        </div>
    );
}

function IconFrame({children}: React.PropsWithChildren) {
    return <div className={'RsnPane__iconFrame'}>{children}</div>;
}

function IconPlaceholder() {
    return <div className={'RsnPane__iconFrame'} />;
}

function TextBlock({children}: React.PropsWithChildren) {
    return <div className={cn('RsnPane__textBlock')}>{children}</div>;
}

const PrimaryText = text('RsnPane__text RsnPane__text--primary');
PrimaryText.displayName = 'RsnPane.PrimaryText';

const SecondaryText = text('RsnPane__text RsnPane__text--secondary');
SecondaryText.displayName = 'RsnPane.SecondaryText';

type TextProps = {children: ReactNode; meta?: ReactNode; truncate?: boolean; stretch?: boolean};

function text(className: string): FunctionComponent<TextProps> {
    const Component: FC<TextProps> = ({children, meta, ...props}) => (
        <div className={className}>
            <span
                className={cn('RsnPane__textWrapper', {
                    'RsnPane__textWrapper--truncate': props.truncate,
                    'RsnPane__textWrapper--stretch': props.stretch,
                })}
            >
                {children}
            </span>
            {meta && <div className={'RsnPane__metaText'}>{meta}</div>}
        </div>
    );
    Component.displayName = 'RsnPane.Text';
    return Component;
}

function MetaBlock(props: React.PropsWithChildren) {
    return <div className='flex flex-col justify-start self-stretch'>{props.children}</div>;
}

function Skeleton() {
    return (
        <div className={'RsnPane__root'}>
            <RsnSkeleton circle width={36} height={36} />
            <div className={'RsnPane__textBlock gap-1'}>
                <RsnSkeleton height={18} width={80} />
                <RsnSkeleton height={14} width={160} />
            </div>
        </div>
    );
}

function Arrow() {
    return <img className='size-5' alt='' src={arrowRightIcon} />;
}

export const RsnPane = Object.assign(
    createPolymorphicComponent('div')<RsnPaneBaseProps>(props => RsnPaneBase(props)),
    {
        Icon,
        SVGIcon,
        IconFrame,
        IconPlaceholder,
        TextBlock,
        MetaBlock,
        PrimaryText,
        SecondaryText,
        Arrow,
        Skeleton,
    }
);
